import { Outlet, useLocation } from "@tanstack/react-router";
import { Menu } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "../../ui";
import { Brand } from "../../ui/Brand";
import { Sheet, SheetContent, SheetTitle } from "../Sheet";

import { CompanyKnowledgeDrawer } from "@/components/company-knowledge/CompanyKnowledgeDrawer";
import { GuidanceProvider } from "@/components/guidance";
import { GuidanceBannerList } from "@/components/guidance/GuidanceBannerList";
import { useChatStore } from "../../../store/chat/chat.store";
import { useStore } from "../../../store/store";
import { AdminSidebar } from "../AdminSidebar";
import { AppBreadcrumb } from "../Navbar/AppBreadcrumb";
import Sidebar from "../Sidebar/Sidebar";

export const AppLayout = () => {
  const fetchProfile = useStore.use.fetchProfile();
  const fetchRoles = useStore.use.fetchRoles();
  const isCompanyKnowledgeOpen = useChatStore((state) => state.isCompanyKnowledgeOpen);
  const companyKnowledgeContext = useChatStore((state) => state.companyKnowledgeContext);
  const closeCompanyKnowledge = useChatStore((state) => state.closeCompanyKnowledge);
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/app/admin");
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);

  useEffect(() => {
    void fetchProfile();
    void fetchRoles();
  }, [fetchProfile, fetchRoles]);

  return (
    <GuidanceProvider>
      <main className="h-screen overflow-hidden">
        {/* Mobile sidebar Sheet */}
        <Sheet open={isMobileSidebarOpen} onOpenChange={setIsMobileSidebarOpen}>
          <SheetContent side="left" className="p-0">
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            <div className="h-full overflow-hidden bg-muted px-4 py-4 dark:bg-background">
              {isAdminRoute ? <AdminSidebar /> : <Sidebar />}
            </div>
          </SheetContent>
        </Sheet>

        <div className="flex h-full overflow-hidden">
          {/* Desktop sidebar — full height, no top offset */}
          <div className={`hidden h-full flex-shrink-0 overflow-hidden bg-muted px-2 py-4 transition-all duration-200 dark:bg-background md:block ${isDesktopSidebarOpen ? "md:w-64 lg:w-80 md:px-4" : "md:w-14"}`}>
            {isAdminRoute
              ? <AdminSidebar isCollapsed={!isDesktopSidebarOpen} />
              : <Sidebar isCollapsed={!isDesktopSidebarOpen} onToggleCollapse={() => setIsDesktopSidebarOpen((v) => !v)} />}
          </div>

          {/* Main content */}
          <div className="flex h-full min-w-0 flex-1 flex-col overflow-hidden bg-background">
            {/* Mobile top bar — Brand + hamburger, hidden on desktop */}
            <div className="flex items-center gap-2 border-b px-4 py-2 md:hidden">
              <Brand to="/app" />
              <Button
                variant="ghost"
                size="icon"
                className="ml-auto shrink-0"
                onClick={() => setIsMobileSidebarOpen(true)}
                aria-label="Open sidebar"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </div>
            {/* Desktop breadcrumb — plain, no bar */}
            <div className="hidden items-center px-6 py-3 md:flex">
              <AppBreadcrumb />
            </div>
            <GuidanceBannerList pathname={location.pathname} />
            <div className="min-h-0 flex-1 overflow-hidden">
              <Outlet />
            </div>
          </div>
        </div>

        <CompanyKnowledgeDrawer
          open={isCompanyKnowledgeOpen}
          onClose={closeCompanyKnowledge}
          context={companyKnowledgeContext}
        />
      </main>
    </GuidanceProvider>
  );
};
